import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';

const ChatMessage = ({ message, isUser }) => (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        {!isUser && (
            <div className="w-8 h-8 rounded-lg mr-2 overflow-hidden">
                <img
                    src="/Images/dio-bot.jpg"
                    alt="DIO Bot"
                    className="w-full h-full object-cover"
                />
            </div>
        )}
        <div className={`max-w-[80%] p-4 rounded-lg ${isUser
                ? 'bg-blue-500/20 text-white rounded-br-none border border-blue-500/20'
                : 'bg-slate-800/50 text-white rounded-bl-none border border-blue-500/20'
            }`}>
            <p className="text-sm">{message}</p>
        </div>
    </div>
);

const Chatbot = () => {
    const [messages, setMessages] = useState([
        {
            text: "Hi! I'm DIO, ready to help refine your business data into actionable insights. What can I assist you with today? 🛢️",
            isUser: false
        }
    ]);
    const [inputText, setInputText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const getBotResponse = async (userInput) => {
        const response = await fetch('/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                messages: [
                    {
                        role: 'system',
                        content: `You are DIO, DataIsOil's friendly digital assistant. You help businesses understand and use their data better.

Our 150 Core Services:

App Development (30):
- Custom business applications
- Mobile apps (iOS/Android)
- Web portals
- E-commerce platforms
- Inventory systems
- Order management
- CRM systems
- HR systems
- Project tracking
- Asset management
- Booking systems
- Payment processing
- Authentication systems
- Notification systems
- Messaging platforms
- Content management
- Document processing
- Workflow automation
- Task management
- Time tracking
- Expense management
- Quality control
- Maintenance tracking
- Resource planning
- Supply chain
- Fleet management
- Customer portals
- Vendor portals
- Employee portals
- Analytics dashboards

Database Solutions (30):
- Database design
- Data warehousing
- Cloud storage
- Backup systems
- Recovery planning
- Data migration
- Performance tuning
- Security setup
- Access control
- Audit logging
- Data archiving
- Version control
- Change tracking
- Query optimization
- Cache systems
- Load balancing
- Failover setup
- Replication
- Data validation
- Error handling
- Data cleaning
- Data enrichment
- Data classification
- Data encryption
- Compliance setup
- Data governance
- Master data management
- Database monitoring
- Storage optimization
- Database maintenance

Integration Services (30):
- System connections
- API development
- Cloud integration
- Email integration
- Payment gateways
- Social media
- Calendar systems
- Document storage
- Voice systems
- Video platforms
- SMS services
- Location services
- Weather services
- Search engines
- Analytics tools
- Marketing platforms
- CRM integration
- ERP integration
- HR systems
- Accounting software
- Shipping services
- Inventory systems
- POS systems
- E-commerce platforms
- Banking systems
- Government services
- IoT devices
- Security systems
- Communication tools
- Collaboration platforms

Business Tools (30):
- Custom reports
- Real-time dashboards
- KPI tracking
- Sales analytics
- Cost analysis
- Revenue tracking
- Inventory reports
- Customer insights
- Market analysis
- Performance metrics
- Trend forecasting
- Risk assessment
- Quality metrics
- Staff analytics
- Project tracking
- Budget monitoring
- Resource planning
- Capacity planning
- Demand forecasting
- Supply chain tracking
- Vendor analytics
- Customer feedback
- Service levels
- Compliance reports
- Audit trails
- Security reports
- System health
- Usage analytics
- ROI calculations
- Business planning

Support (30):
- Technical support
- User training
- System updates
- Bug fixes
- Performance tuning
- Security patches
- Data backup
- Recovery help
- User guides
- Video tutorials
- Help desk
- Chat support
- Email support
- Phone support
- Remote support
- Onsite support
- Emergency response
- Preventive maintenance
- System monitoring
- Network support
- Desktop support
- Mobile support
- Cloud support
- Database support
- Application support
- Integration support
- Security support
- Compliance support
- Training materials
- Knowledge base

Style:
- Friendly and approachable
- Avoid technical jargon
- Use simple explanations
- Keep responses under 100 words
- Ask ONE relevant follow-up question when appropriate
- For complex needs, direct to https://dioplus.com.au/contact
- Use oil/refinery metaphors sparingly`
                    },
                    ...messages.map(msg => ({
                        role: msg.isUser ? 'user' : 'assistant',
                        content: msg.text
                    })),
                    { role: 'user', content: userInput }
                ]
            })
        });

        if (!response.ok) throw new Error('Failed to get response');
        const data = await response.json();
        return data.message;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputText.trim() || isProcessing) return;

        const userMessage = { text: inputText, isUser: true };
        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsProcessing(true);

        try {
            const response = await getBotResponse(inputText);
            setMessages(prev => [...prev, { text: response, isUser: false }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                text: "Connection error. Please try again or contact us directly.",
                isUser: false
            }]);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="bg-slate-800/50 rounded-lg p-6 min-h-[600px] flex flex-col border border-blue-500/20">
            <div className="flex-1 overflow-y-auto mb-4">
                {messages.map((message, index) => (
                    <ChatMessage
                        key={index}
                        message={message.text}
                        isUser={message.isUser}
                    />
                ))}
                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="How can we help refine your business data?"
                    className="flex-1 px-4 py-3 rounded-lg bg-slate-700 text-white text-sm placeholder-slate-400 
                             border border-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    disabled={isProcessing}
                />
                <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-lg 
                             transition-colors disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/50"
                    disabled={!inputText.trim() || isProcessing}
                >
                    <Send className="w-5 h-5" />
                </button>
            </form>
        </div>
    );
};

export default Chatbot;